import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ROLE, normaliseRole, getSubjectClaims } from '../roles';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { GraduationCap } from 'lucide-react';
import { db, auth } from '../firebase';
import TalentBankReport from './TalentBankReport';
import { deriveCreditBalances } from '../talentBank';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// My Talent Bank: a student's own record, or a guardian's child's.
//
// A separate module from the staff Talent Bank rather than a role branch inside it. Partly so a
// school can switch this on for students without exposing the staff screen's class filter and roster
// search -- but mostly because the queries are genuinely different. A family must filter on the email
// field its security rule tests (studentEmail, or guardianEmails for a guardian); asking by sid the
// way staff do gets the WHOLE query refused, not trimmed. So the question "whose data may I ask for"
// is answered here, and the report is handed one student's results.
const MyTalentBank = () => {
  const { activeSchoolId, userProfile } = useOutletContext();
  const { t } = useT();

  const ownRole = normaliseRole(userProfile?.role);
  const isParent = ownRole === ROLE.PARENT;
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownSid = userProfile?.studentId || userProfile?.sid || '';

  // WHO YOU MAY LOOK AT comes from the ID token, never from a profile list. syncAccountClaims stamps
  // a student's own `sid` and a guardian's `childSids`, and the security rules test exactly those --
  // so a picker built from the same claim can never offer somebody the rules would refuse.
  const [subjectClaims, setSubjectClaims] = useState(null);
  useEffect(() => { getSubjectClaims(auth.currentUser).then(setSubjectClaims); }, []);

  // Children are labelled from userProfile.children but INTERSECTED with childSids: if the claim has
  // not caught up with a newly linked child, that child's data would be denied, and offering them
  // produces an unexplained empty page rather than an honest absence.
  const ownChildren = useMemo(() => {
    if (!isParent || !subjectClaims) return [];
    const allowed = new Set(subjectClaims.childSids || []);
    return (userProfile?.children || []).filter(c => c.sid && allowed.has(c.sid));
  }, [isParent, subjectClaims, userProfile]);

  const [selectedSid, setSelectedSid] = useState('');

  // A student has no choice to make; a guardian starts on their first child and may switch.
  useEffect(() => {
    if (!isParent && ownSid) setSelectedSid(ownSid);
  }, [isParent, ownSid]);

  useEffect(() => {
    if (!isParent || ownChildren.length === 0) return;
    if (!ownChildren.some(c => c.sid === selectedSid)) setSelectedSid(ownChildren[0].sid);
  }, [isParent, ownChildren, selectedSid]);

  const selectedMeta = useMemo(() => {
    if (isParent) {
      const child = ownChildren.find(c => c.sid === selectedSid);
      return child ? {
        sid: child.sid,
        name: child.name || child.sid,
        chineseName: child.chineseName || '',
        currentClass: child.currentClass || '',
        classNumber: child.classNumber || '',
      } : null;
    }
    return ownSid ? {
      sid: ownSid,
      name: userProfile?.name || 'Student',
      chineseName: userProfile?.chineseName || '',
      currentClass: userProfile?.currentClass || '',
      classNumber: userProfile?.classNumber || '',
    } : null;
  }, [isParent, ownChildren, selectedSid, ownSid, userProfile]);

  // --- OLE: families read ole_participant, one row per student ---
  // The activity document itself carries the whole roster plus every participant's role and
  // attendance, so it is staff-only. A guardian's query returns every child's rows, which is why the
  // results are narrowed to the selected child afterwards.
  const [oleActivities, setOleActivities] = useState([]);
  const [loadingOle, setLoadingOle] = useState(false);

  useEffect(() => {
    if (!activeSchoolId || !selectedSid || !ownEmail) { setOleActivities([]); return; }
    setLoadingOle(true);
    const col = collection(db, 'schools', activeSchoolId, 'ole_participant');
    const q = isParent
      ? query(col, where('guardianEmails', 'array-contains', ownEmail))
      : query(col, where('studentEmail', '==', ownEmail));
    getDocs(q)
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setOleActivities(list.filter(a => a.sid === selectedSid));
      })
      .catch(e => { console.error('Error loading OLE records', e); setOleActivities([]); })
      .finally(() => setLoadingOle(false));
  }, [activeSchoolId, selectedSid, isParent, ownEmail]);

  // --- Conduct records, scoped the same way ---
  const [creditRecords, setCreditRecords] = useState([]);
  const [loadingCredit, setLoadingCredit] = useState(false);

  useEffect(() => {
    if (!activeSchoolId || !selectedSid || !ownEmail) { setCreditRecords([]); return; }
    setLoadingCredit(true);
    const col = collection(db, 'schools', activeSchoolId, 'student_credit_record');
    const q = isParent
      ? query(col, where('guardianEmails', 'array-contains', ownEmail))
      : query(col, where('studentEmail', '==', ownEmail));
    getDocs(q)
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        const scoped = list.filter(r => r.sid === selectedSid);
        scoped.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setCreditRecords(scoped);
      })
      .catch(e => { console.error('Error loading student credit records', e); setCreditRecords([]); })
      .finally(() => setLoadingCredit(false));
  }, [activeSchoolId, selectedSid, isParent, ownEmail]);

  // Derived rather than read from metadata/student_credit_balances: that document holds every
  // student's balance in one map, and a rule cannot hand back one key of a map. Same arithmetic --
  // the aggregate is itself just balanceChange summed and grouped by year.
  const creditBalances = useMemo(() => deriveCreditBalances(creditRecords), [creditRecords]);

  return (
    <TalentBankReport
      sid={selectedSid}
      studentMeta={selectedMeta}
      oleActivities={oleActivities}
      loadingOle={loadingOle}
      creditRecords={creditRecords}
      loadingCredit={loadingCredit}
      creditBalances={creditBalances}
      // Nothing to select and nothing to search: if this shows, the account has no student record
      // linked to it yet, which the picker also says. Says what is true rather than offering an
      // action that does not exist.
      emptyState={(
        <>
          <GraduationCap size={64} style={{ opacity: 0.1, marginBottom: '1.5rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>{t('family.nothingYet')}</h3>
          <p style={{ maxWidth: '38ch', lineHeight: 1.6 }}>
            {isParent ? t('family.noChildLinked') : t('family.noOwnRecord')}
          </p>
        </>
      )}
      pickerSlot={
        isParent ? (
          ownChildren.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: 'var(--color-danger)', whiteSpace: 'nowrap' }}>
              {t('family.noChildLinkedShort')}
            </span>
          ) : ownChildren.length > 1 ? (
            <select
              className="input-field"
              aria-label={t('family.child')}
              value={selectedSid}
              onChange={e => setSelectedSid(e.target.value)}
              style={{ width: '240px', flexShrink: 0 }}
            >
              {ownChildren.map(c => (
                <option key={c.sid} value={c.sid}>
                  {c.currentClass} ({c.classNumber}) - {c.name} ({c.sid})
                </option>
              ))}
            </select>
          ) : null
        ) : (
          !ownSid && (
            <span style={{ fontSize: '0.8rem', color: 'var(--color-danger)', whiteSpace: 'nowrap' }}>
              {t('family.noOwnRecordShort')}
            </span>
          )
        )
      }
    />
  );
};

export default MyTalentBank;
